/**
 * How the customer app reaches the Platform API (FastAPI).
 *
 * - Production: set VITE_BACKEND_API_URL to your API origin (e.g. https://api.example.com).
 * - Development (recommended for clarity): set VITE_BACKEND_API_URL=http://127.0.0.1:8000 so fetch targets
 *   the API directly (browser DevTools shows :8000). The API must allow CORS for the dev origin.
 * - Development (alternative): leave VITE_BACKEND_API_URL empty — requests use same-origin `/api/...` on the
 *   Vite dev server (e.g. :3000) and vite.config.ts proxies `/api` to http://127.0.0.1:8000.
 * - Offline demo: VITE_USE_LOCAL_USER_DB=true and/or VITE_USE_LOCAL_OTP=true.
 */

export function getBackendBaseUrl(): string {
  const explicit = (import.meta.env.VITE_BACKEND_API_URL || '').trim().replace(/\/+$/, '');
  if (explicit) return explicit;
  if (import.meta.env.DEV) return '';
  return '';
}

/**
 * Use PostgreSQL-backed signup/login (Platform API).
 *
 * Must be true in production when the app is served behind nginx / same-origin `/api`
 * even if `VITE_BACKEND_API_URL` is empty (browser uses relative `/api/...`).
 * Previously only DEV or an explicit URL enabled this — production builds then skipped
 * `customerLogin` and never stored JWTs, causing 401 on team routes.
 */
export function isBackendUserDbEnabled(): boolean {
  if (import.meta.env.VITE_USE_LOCAL_USER_DB === 'true') return false;
  if ((import.meta.env.VITE_BACKEND_API_URL || '').trim().length > 0) return true;
  if (import.meta.env.PROD) return true;
  return import.meta.env.DEV === true;
}

/** Use server OTP endpoints (requires running API unless local OTP is forced). */
export function isBackendOtpEnabled(): boolean {
  if (import.meta.env.VITE_USE_LOCAL_OTP === 'true') return false;
  if ((import.meta.env.VITE_BACKEND_API_URL || '').trim().length > 0) return true;
  if (import.meta.env.PROD) return true;
  return import.meta.env.DEV === true;
}

/** Human-readable hint when fetch fails (connection refused, DNS, etc.). */
export function apiConnectionHint(displayBase: string): string {
  const devProxy =
    import.meta.env.DEV && !(import.meta.env.VITE_BACKEND_API_URL || '').trim()
      ? ' This project uses the Vite dev proxy: leave VITE_BACKEND_API_URL empty so calls go to /api on this dev server, and run the API on port 8000.'
      : '';
  const devProxyIfDirect =
    import.meta.env.DEV && (import.meta.env.VITE_BACKEND_API_URL || '').trim()
      ? ' If the API is already running, open http://127.0.0.1:8000/health in a tab, or clear VITE_BACKEND_API_URL to use same-origin /api (Vite proxy) and avoid cross-origin issues.'
      : '';
  return (
    `Cannot reach the Platform API (${displayBase || 'same-origin /api'}). ` +
    `The browser failed before any HTTP response (not a 4xx/5xx from the server). ` +
    `Start the API from the monorepo: npm run dev:backend (port 8000), or set VITE_BACKEND_API_URL to your deployed API.` +
    devProxy +
    devProxyIfDirect +
    ` For offline demo signup, set VITE_USE_LOCAL_USER_DB=true (and optionally VITE_USE_LOCAL_OTP=true).`
  );
}

/**
 * Controls whether UI services may use mock/fallback data.
 *
 * - Set VITE_ALLOW_MOCK_DATA=false to enforce real backend-only data.
 * - Default is true in development for UI/testing convenience.
 * - Default is false in production unless explicitly set true.
 */
export function isMockDataAllowed(): boolean {
  const raw = (import.meta.env.VITE_ALLOW_MOCK_DATA || '').toString().trim().toLowerCase();
  if (raw) return ['1', 'true', 'yes', 'on'].includes(raw);
  return import.meta.env.DEV === true;
}
